import type { ConflictV2 } from "@classroom/domain";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Purpose, Root } from "../../../../../supabase/functions/sync-control/model";
import type { LocalAccount } from "../localStore/accountScope";
import { hasPendingLocalWrites, LocalStoreClient } from "../localStore/client";
import type { LocalSyncState, SyncAttachment } from "../localStore/syncV2Store";
import { newWorkspace } from "../localStore/useLocalWorkspace";
import { type ControlSummary, SyncControlClient } from "./controlClient";
import { canonical, createDeviceKeys, type ObjectScope, openDeviceEnvelope, sealDeviceEnvelope } from "./cryptoV2";
import { activateDeviceSetup } from "./deviceSetup";
import { DeviceVault, type UnlockedDevice } from "./deviceVault";
import { disconnectDevice } from "./disconnectDevice";
import { GoogleDriveV2 } from "./driveV2";
import type { RecoveryDraft } from "./recoveryDraft";
import { syncReviewId } from "./reviewScope";
import { retryDelay, SynchronizeV2, type SyncProgress, type SyncRuntimeStore } from "./synchronizeV2";

export type VerifyOperation = (purpose: Purpose, target: unknown) => Promise<string>;
export type ReviewStatus = {
  local: LocalSyncState | null;
  summary: ControlSummary | null;
  pending: number;
  quarantined: number;
  conflicts: ConflictV2[];
  concurrentEditing: boolean;
  unlocked: boolean;
  busy: boolean;
  error: string;
  ready: boolean;
  setupPending: boolean;
  progress: SyncProgress | null;
  progressAt: number;
};
export { dataKeyScope } from "./syncScopes";

import { rebindDrive } from "./rebindDrive";
import { commitManagedRecovery, type ManageMode, prepareManagedRecovery } from "./syncManagement";
import { dataKeyScope } from "./syncScopes";

const attachment = (device: UnlockedDevice): SyncAttachment => ({
  datasetId: device.scope.datasetId,
  generation: device.scope.generation,
  keyVersion: device.scope.keyVersion,
  deviceId: device.deviceId,
});

export function useSyncReview(account: LocalAccount, auth: SupabaseClient) {
  const reviewId = syncReviewId() ?? "app";
  const vault = useMemo(() => new DeviceVault(`classroom-sync-v2-${reviewId}`), [reviewId, account.workspaceId]);
  const [client, setClient] = useState<LocalStoreClient | null>(null);
  useEffect(() => {
    const connection = new LocalStoreClient(account);
    setClient(connection);
    return () => connection.close();
  }, [account.workspaceId]);
  const endpoint =
    import.meta.env.VITE_SYNC_CONTROL_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-control`;
  const configured = Boolean(import.meta.env.VITE_SUPABASE_URL);
  const [status, setStatus] = useState<ReviewStatus>({
    local: null,
    summary: null,
    pending: 0,
    quarantined: 0,
    conflicts: [],
    concurrentEditing: false,
    unlocked: false,
    busy: false,
    error: "",
    ready: false,
    setupPending: false,
    progress: null,
    progressAt: 0,
  });
  const state = useRef(status);
  state.current = status;
  const initializing = useRef(true);
  const device = useRef<UnlockedDevice | null>(null);
  const candidate = useRef<Awaited<ReturnType<typeof createDeviceKeys>> | null>(null);
  const candidateId = useRef<string>(crypto.randomUUID());
  const api = useRef<SyncControlClient | null>(null);
  const engine = useRef<SynchronizeV2 | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);
  const live = useRef(true);
  const running = useRef<Promise<string | undefined> | null>(null);
  const managing = useRef(false);
  const managementTask = useRef(false);
  const rerun = useRef(false);
  const retry = useRef({ attempt: 0, at: 0 });
  const schedule = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, tick] = useState(0);

  const update = useCallback((patch: Partial<ReviewStatus>) => {
    if (live.current)
      setStatus((current) => {
        const next = { ...current, ...patch };
        state.current = next;
        return next;
      });
  }, []);
  const refresh = useCallback(async () => {
    if (!client) throw new Error("sync-storage-not-ready");
    const local = await client.syncState(account.workspaceId);
    const counts = await client.syncStats(account.workspaceId);
    const conflicts = await client.syncConflicts(account.workspaceId);
    update({ local, ...counts, conflicts, unlocked: !!device.current, ready: !initializing.current });
    return local;
  }, [client, account.workspaceId, update]);
  const connectApi = useCallback(
    (identity: Pick<UnlockedDevice, "deviceId" | "signing" | "agreement">) => {
      const value = new SyncControlClient(auth, endpoint, {
        ...identity,
        accountId: account.userId,
        label: `${navigator.platform || "Browser"} · ${reviewId}`,
      });
      api.current = value;
      return value;
    },
    [auth, endpoint, account.userId, reviewId],
  );

  const run = useCallback(async () => {
    if (!client) return "sync-storage-not-ready";
    if (managing.current) return "sync-maintenance";
    if (running.current) {
      rerun.current = true;
      return running.current;
    }
    if (!device.current || !api.current || !configured) return "sync-locked";
    if (document.hidden || !navigator.onLine) return "sync-paused";
    if (Date.now() < retry.current.at) return state.current.error || "sync-paused";
    const job = (async () => {
      update({ busy: true, error: "", progress: { phase: "checking", completed: 0 }, progressAt: Date.now() });
      try {
        let local = await client.syncState(account.workspaceId);
        if (!local || local.status === "detached" || local.maintenance) return "sync-locked";
        const summary = await api.current!.devices();
        update({ summary });
        if (
          !summary.root ||
          summary.root.datasetId !== local.datasetId ||
          summary.root.generation !== local.generation
        ) {
          await client.syncRemoteGeneration(account.workspaceId, local);
          throw new Error("sync-generation-mismatch");
        }
        if (local.status !== "active") {
          await client.syncResume(account.workspaceId);
          local = (await client.syncState(account.workspaceId))!;
        }
        if (summary.root.keyVersion !== local.keyVersion) {
          const current = device.current!;
          const envelope = summary.root.deviceEnvelopes[current.deviceId] as
            | Awaited<ReturnType<typeof sealDeviceEnvelope>>
            | undefined;
          if (!envelope) throw new Error("sync-device-revoked");
          const scope = dataKeyScope(summary.root);
          const material = (await openDeviceEnvelope(
            current.agreement.privateKey,
            { ...scope, objectId: current.deviceId },
            envelope,
          )) as { dataKey: string };
          const unlocked = await vault.create(
            { accountId: current.accountId, deviceId: current.deviceId },
            scope,
            material.dataKey,
            current.remembered,
            current,
          );
          await client.syncRekey(account.workspaceId, attachment(unlocked), unlocked.dataKey);
          device.current = unlocked;
          engine.current = null;
          local = (await client.syncState(account.workspaceId))!;
        }
        if (!summary.drive) return "sync-google-reconnect";
        const registry = async () => {
          const value = await api.current!.devices();
          if (!value.root) throw new Error("sync-root-unavailable");
          return { root: value.root, devices: value.devices, revokedClocks: value.root.revokedClocks };
        };
        if (!engine.current) {
          const store: SyncRuntimeStore = {
            state: () => client.syncState(account.workspaceId),
            pending: () => client.syncPending(account.workspaceId),
            operation: (id) => client.syncOperation(account.workspaceId, id),
            allocateFile: (id, fileId) => client.syncAllocate(account.workspaceId, id, fileId),
            acknowledge: (op, fileId) => client.syncAcknowledge(account.workspaceId, op, fileId),
            receive: async (ops) => {
              const result = await client.syncReceive(account.workspaceId, ops);
              if (result.concurrentDevices.length) update({ concurrentEditing: true });
              return result;
            },
            cursor: (cursor, expected) => client.syncCursor(account.workspaceId, cursor, expected),
            stage: (ops) => client.syncStage(account.workspaceId, ops),
            drain: async (final) => {
              const devices = await client.syncDrain(account.workspaceId, final);
              if (devices.length) update({ concurrentEditing: true });
            },
            checkpointPage: (after, clock) =>
              client.syncCheckpoint(account.workspaceId, { action: "page", after, clock }),
            checkpointSink: {
              begin: (id, clock) => client.syncCheckpoint(account.workspaceId, { action: "begin", id, clock }),
              stage: (id, cells) => client.syncCheckpoint(account.workspaceId, { action: "stage", id, cells }),
              commit: (id) => client.syncCheckpoint(account.workspaceId, { action: "commit", id }),
              abort: (id) => client.syncCheckpoint(account.workspaceId, { action: "abort", id }),
            },
            checkpointAdvanceSink: (expected) => ({
              begin: (id, clock) => client.syncCheckpoint(account.workspaceId, { action: "begin", id, clock }),
              stage: (id, cells) => client.syncCheckpoint(account.workspaceId, { action: "stage", id, cells }),
              commit: async (id) => {
                if (hasPendingLocalWrites()) throw new Error("sync-draft-save-required");
                await client.syncCheckpoint(account.workspaceId, { action: "advance", id, expected });
              },
              abort: (id) => client.syncCheckpoint(account.workspaceId, { action: "abort", id }),
            }),
          };
          engine.current = new SynchronizeV2(
            store,
            new GoogleDriveV2(() => api.current!.driveToken()),
            registry,
            {
              signing: device.current!.signing.privateKey,
              key: async (version) =>
                (await vault.unlock(account.userId, dataKeyScope({ ...local, keyVersion: version })))?.dataKey ?? null,
            },
            () => !document.hidden && navigator.onLine,
            (progress) => update({ progress, progressAt: Date.now() }),
          );
        }
        await engine.current.run();
        await client.syncOnline(account.workspaceId, Date.now() + 30000, local);
        await engine.current
          .checkpoint(async (checkpoint, expectedHash, expected) => {
            await api.current!.request("checkpoint-publish", {
              checkpoint,
              expectedHash,
              datasetId: expected.datasetId,
              generation: expected.generation,
              keyVersion: expected.keyVersion,
            });
          })
          .catch((error) => {
            if (
              !(error instanceof Error) ||
              !["sync-checkpoint-not-ready", "sync-checkpoint-stale", "sync-control-race"].includes(error.message)
            )
              throw error;
          });
        retry.current = { attempt: 0, at: 0 };
        update({ error: "" });
        channel.current?.postMessage({ workspaceId: account.workspaceId });
      } catch (error) {
        const code =
          error instanceof Error && /^sync-[a-z-]+$/.test(error.message) ? error.message : "sync-control-unavailable";
        if (["sync-account-denied", "sync-device-revoked", "sync-sign-in-required"].includes(code))
          await client.syncDeny(account.workspaceId).catch(() => undefined);
        else await client.syncDisconnect(account.workspaceId).catch(() => undefined);
        if (code === "sync-google-reconnect") api.current?.clearTransport();
        update({ error: code });
        if (
          [
            "sync-drive-request-failed",
            "sync-control-unavailable",
            "sync-control-race",
            "sync-missing-parent",
            "sync-rate-limit",
            "sync-upload-unconfirmed",
          ].includes(code)
        ) {
          retry.current = { attempt: retry.current.attempt + 1, at: Date.now() + retryDelay(retry.current.attempt) };
          rerun.current = true;
        }
        return code;
      } finally {
        await refresh().catch(() => undefined);
        update({ busy: false });
      }
    })();
    running.current = job;
    try {
      return await job;
    } finally {
      running.current = null;
    }
  }, [client, account.workspaceId, account.userId, vault, configured, refresh, update]);
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    if (!client) return;
    live.current = true;
    initializing.current = true;
    channel.current = new BroadcastChannel(`classroom-local-workspace-changes:${account.workspaceId}`);
    let cancelled = false;
    void (async () => {
      await client.accountStatus();
      if (cancelled) return;
      await client.ensureWorkspace(newWorkspace(account.workspaceId, account.userId) as unknown as { id: string });
      if (cancelled) return;
      const local = await client.syncState(account.workspaceId);
      if (local) {
        const remembered = await vault.unlock(account.userId, dataKeyScope(local));
        if (remembered && !cancelled) {
          device.current = remembered;
          connectApi(remembered);
          if (local.status !== "detached")
            await client.syncUnlock(account.workspaceId, attachment(remembered), remembered.dataKey);
        }
      }
      if (!api.current) {
        const pending = !local ? await vault.pendingSetup(account.userId) : null;
        candidate.current = pending ?? (await createDeviceKeys());
        if (cancelled) return;
        if (pending) {
          candidateId.current = pending.deviceId;
          update({ setupPending: true });
        }
        connectApi({ ...candidate.current, deviceId: candidateId.current });
      }
      await refresh();
      if (configured && !device.current && api.current) {
        const value = await api.current.accountState();
        if (!cancelled)
          update({ summary: { devices: [], root: value.root, drive: value.drive, oauthCandidate: null } });
      }
      if (device.current && configured) {
        await runRef.current();
      }
      // Finish any remote key adoption before mounting the console's Workers.
      // Otherwise their initial unlock can race the controller's local rekey.
      if (!cancelled) {
        initializing.current = false;
        await refresh();
      }
    })().catch((error) => {
      if (!cancelled) {
        initializing.current = false;
        update({
          error:
            error instanceof Error && /^sync-[a-z-]+$/.test(error.message) ? error.message : "sync-storage-unavailable",
          ready: true,
        });
      }
    });
    const saved = (event: Event) => {
      if ((event as CustomEvent).detail?.workspaceId !== account.workspaceId) return;
      void refresh().catch(() => undefined);
      if (!schedule.current)
        schedule.current = setTimeout(() => {
          schedule.current = null;
          void runRef.current();
        }, 2000);
    };
    const foreground = () => {
      if (!document.hidden) void runRef.current();
    };
    const offline = () => {
      void client
        .syncDisconnect(account.workspaceId)
        .then(refresh)
        .catch(() => undefined);
    };
    window.addEventListener("classroom-sync-v2-saved", saved);
    window.addEventListener("online", foreground);
    window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", foreground);
    const timer = setInterval(() => {
      tick((n) => n + 1);
      if (rerun.current && !running.current) {
        rerun.current = false;
        void runRef.current();
      }
    }, 1000);
    const poll = setInterval(() => void runRef.current(), 15000);
    return () => {
      cancelled = true;
      live.current = false;
      clearInterval(timer);
      clearInterval(poll);
      if (schedule.current) clearTimeout(schedule.current);
      window.removeEventListener("classroom-sync-v2-saved", saved);
      window.removeEventListener("online", foreground);
      window.removeEventListener("offline", offline);
      document.removeEventListener("visibilitychange", foreground);
      channel.current?.close();
      channel.current = null;
      client.clearSyncSession();
      client.close();
      vault.lock();
    };
  }, [client, vault, account.workspaceId, account.userId, connectApi, refresh, update]);

  const assertClassroom = useCallback(() => {
    const s = state.current;
    if (!s.local) throw new Error("sync-connection-required");
    if (
      !s.unlocked ||
      s.local.status !== "active" ||
      s.local.maintenance ||
      !navigator.onLine ||
      Date.now() >= s.local.onlineUntil
    )
      throw new Error("sync-connection-required");
  }, []);
  const activateNew = useCallback(
    async (draft: RecoveryDraft, remember: boolean, verify: VerifyOperation) => {
      if (hasPendingLocalWrites()) throw new Error("sync-draft-save-required");
      if (!client || !api.current || !candidate.current) throw new Error("sync-setup-unavailable");
      const material = draft.verifiedMaterial();
      const scope = dataKeyScope(material);
      const envelope = await sealDeviceEnvelope(
        await crypto.subtle.exportKey("jwk", candidate.current.agreement.publicKey),
        { ...scope, objectId: candidateId.current },
        { dataKey: material.dataKey },
      );
      const root = {
        datasetId: material.datasetId,
        generation: material.generation,
        keyVersion: material.keyVersion,
        recoveryPublicKey: draft.publicKey,
        deviceEnvelopes: { [candidateId.current]: envelope },
      };
      const existing = await api.current.devices();
      if (existing.root) {
        if (
          existing.root.datasetId !== root.datasetId ||
          existing.root.generation !== root.generation ||
          existing.root.keyVersion !== root.keyVersion ||
          canonical(existing.root.recoveryPublicKey) !== canonical(root.recoveryPublicKey)
        )
          throw new Error("sync-root-exists");
        if (existing.drive && !(await client.syncState(account.workspaceId)))
          throw new Error("sync-existing-data-confirmation-required");
        const previous = existing.root.deviceEnvelopes[candidateId.current] as Awaited<
          ReturnType<typeof sealDeviceEnvelope>
        >;
        const read = (await openDeviceEnvelope(
          candidate.current.agreement.privateKey,
          { ...scope, objectId: candidateId.current },
          previous,
        )) as { dataKey?: string };
        if (read.dataKey !== material.dataKey) throw new Error("sync-key-scope");
      } else {
        const proofId = await verify("recovery-file", { root });
        await api.current.request("root-create", {
          root,
          proofId,
          recoverySignature: await draft.sign({ accountId: account.userId, root }),
        });
      }
      const unlocked = await vault.create(
        { accountId: account.userId, deviceId: candidateId.current },
        scope,
        material.dataKey,
        remember,
        candidate.current,
      );
      device.current = unlocked;
      connectApi(unlocked);
      await client.syncCreateLocal(account.workspaceId, attachment(unlocked), unlocked.dataKey);
      if (remember) localStorage.setItem(`classroom-sync-v2-offline-account:${reviewId}`, JSON.stringify(account));
      draft.dispose();
      await refresh();
    },
    [account, client, vault, connectApi, reviewId, refresh],
  );
  const activateAutomatic = useCallback(async () => {
    if (hasPendingLocalWrites()) throw new Error("sync-draft-save-required");
    if (!client || !api.current || !candidate.current) throw new Error("sync-setup-unavailable");
    const unlocked = await activateDeviceSetup(api.current, vault, client, account.workspaceId, candidate.current);
    device.current = unlocked;
    connectApi(unlocked);
    localStorage.setItem(`classroom-sync-v2-offline-account:${reviewId}`, JSON.stringify(account));
    update({ setupPending: false, error: "", summary: await api.current!.devices() });
    await refresh();
  }, [account, client, vault, connectApi, reviewId, refresh, update]);
  const join = useCallback(
    async (root: Root, material: string, remember: boolean, receipt?: string) => {
      if (hasPendingLocalWrites()) throw new Error("sync-draft-save-required");
      if (!client || !api.current) throw new Error("sync-setup-unavailable");
      const current = api.current.device;
      const keys = candidate.current ?? device.current;
      if (!keys) throw new Error("sync-locked");
      const unlocked = await vault.create(
        { accountId: account.userId, deviceId: current.deviceId },
        dataKeyScope(root),
        material,
        remember,
        keys,
      );
      await client.syncJoin(account.workspaceId, attachment(unlocked), unlocked.dataKey, receipt);
      device.current = unlocked;
      connectApi(unlocked);
      engine.current = null;
      if (remember) localStorage.setItem(`classroom-sync-v2-offline-account:${reviewId}`, JSON.stringify(account));
      await refresh();
      return await runRef.current();
    },
    [account, client, vault, connectApi, reviewId, refresh],
  );
  const management = () => {
    if (!client || !api.current || !device.current) throw new Error("sync-locked");
    return {
      api: api.current,
      device: device.current,
      vault,
      client,
      workspaceId: account.workspaceId,
      engine: engine.current,
    };
  };
  const adoptIdentity = async (root: Root, material: string, remember: boolean) => {
    if (!api.current) throw new Error("sync-setup-unavailable");
    const keys = candidate.current ?? device.current;
    if (!keys) throw new Error("sync-locked");
    const identity = { accountId: account.userId, deviceId: api.current.device.deviceId };
    const unlocked = await vault.create(identity, dataKeyScope(root), material, remember, keys);
    device.current = unlocked;
    connectApi(unlocked);
    update({ unlocked: true, summary: await api.current.devices() });
    await refresh();
  };
  const prepareManagement = (mode: ManageMode, password: string) =>
    prepareManagedRecovery(management(), mode, password);
  const commitManagement = async (
    mode: ManageMode,
    draft: RecoveryDraft,
    verify: VerifyOperation,
    options: Parameters<typeof commitManagedRecovery>[4],
  ) => {
    if ((mode === "generation" || mode === "rebind") && hasPendingLocalWrites())
      throw new Error("sync-draft-save-required");
    if (mode !== "generation") await runRef.current();
    const unlocked =
      mode === "rebind"
        ? await rebindDrive(management(), draft, verify, {
            receipt: options?.receipt ?? "",
            backupChecksum: options?.backupChecksum ?? "",
            confirmedSubject: options?.confirmedSubject ?? "",
          })
        : await commitManagedRecovery(management(), mode, draft, verify, options);
    device.current = unlocked;
    connectApi(unlocked);
    engine.current = null;
    await refresh();
    await runRef.current();
  };
  const disconnect = async (deviceId: string, draft?: RecoveryDraft) => {
    if (managementTask.current || hasPendingLocalWrites()) throw new Error("sync-draft-save-required");
    managementTask.current = true;
    try {
      const failure = await runRef.current();
      if (failure) throw new Error(failure);
      managing.current = true;
      update({ busy: true });
      await disconnectDevice(management(), deviceId, draft);
    } finally {
      managing.current = false;
      managementTask.current = false;
      update({ busy: false });
      // Adopt the committed envelope even if the reply or a local rekey failed.
      await runRef.current();
    }
  };
  return {
    status,
    configured,
    client,
    vault,
    api,
    device,
    run,
    refresh,
    dismissConcurrentEditing: () => update({ concurrentEditing: false }),
    inspectAccount: async () => {
      if (!api.current) throw new Error("sync-setup-unavailable");
      update({ summary: await api.current.devices() });
    },
    activateNew,
    activateAutomatic,
    join,
    prepareManagement,
    commitManagement,
    disconnect,
    adoptIdentity,
    assertClassroom,
  };
}
